'use client';

import { motion, AnimatePresence } from 'framer-motion';

interface QuestionPromptProps {
  questionText: string;
  topic: string;
  isFollowup: boolean;
  isRecording: boolean;
  clarificationLeadIn?: string | null;
  guidanceText?: string | null;
}

export function QuestionPrompt({
  questionText,
  isFollowup,
  isRecording,
  clarificationLeadIn,
  guidanceText,
}: QuestionPromptProps) {
  return (
    <div className="px-6 text-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={`${clarificationLeadIn ?? ''}::${questionText}`}
          data-ph-no-capture
          initial={isFollowup ? { opacity: 0, x: 20 } : { opacity: 0, y: 16 }}
          // While the member answers (listening) or waits (thinking), the
          // question steps back but stays readable: they may glance up at it.
          animate={{ opacity: isRecording ? 0.6 : 1, x: 0, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {clarificationLeadIn && (
            <p
              data-testid="question-clarification"
              className="mb-2 font-body text-sm text-text-secondary max-w-sm mx-auto"
            >
              {clarificationLeadIn}
            </p>
          )}
          <p
            role="heading"
            aria-level={2}
            data-testid="question-prompt"
            className="font-heading text-[20px] font-normal leading-[1.4] tracking-[0.01em] text-text-primary max-w-md mx-auto"
          >
            {questionText}
          </p>
          {guidanceText && (
            <p
              data-testid="question-guidance"
              className="mt-2 font-body text-sm text-text-secondary max-w-sm mx-auto"
            >
              {guidanceText}
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
